import type { MaybeRefOrGetter, Ref } from 'vue';
import { computed, getCurrentScope, onScopeDispose, toValue, watch } from 'vue';
import { StatusCodes } from 'http-status-codes';
import type { NuxtError } from 'nuxt/app';
import type { Entry } from 'contentful';
import type {
  EntryWithLinkData,
  IContentPage,
  LOCALE_CODE
} from '~~/lib/ContentfulService';
import type { MinifiedSecondaryNavigation } from '~/types/MinifiedSecondaryNavigation';
import type { PageTeaserMeta } from '~/types/PageTeaserMeta';
import {
  buildPageTeaserMetaCacheKey,
  getRequestScopedPageTeaserMeta
} from '~/utils/contentful/requestScopedPageTeaserMeta';
import { ContentfulTransformerService } from '~~/lib/ContentfulTransformerService/ContentfulTransformerService';
import { resolveEntry } from '~/utils/resolveEntry';
import { Tracer } from '~~/lib/Tracing';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import type { TocItem } from '~/utils/extractTocItems';
import { usePreloadedContentfulPage } from '~/composables/usePreloadedContentfulPage';
import type { ContentfulPageResponseModel } from '~~/server/api/contentful/index.get';
import { applyContentfulAssetCdnByLocale } from '~/utils/contentful/rewriteContentfulAssetHostsByLocale';
import { collapseContentCenterSlug } from '~/utils/contentful/collapseContentCenterSlug';
import { isDefined } from '~/utils/guards/isDefined';
import type { BreadcrumbEntry } from '~/types/MinifiedNavigation';
import { usePaywall } from '~/composables/usePaywall';
import { containsPaywall } from '~/utils/paywall/containsPaywall';
import { getPaywall } from '~/utils/paywall/getPaywall';
import { isLeadPaywall } from '~/utils/paywall/isLeadPaywall';
import { moveAllEntriesAfterPaywallIntoPaywall } from '~/utils/paywall/moveAllEntriesAfterPaywallIntoPaywall';
import {
  GATED_CONTENT_ACCESS_QUERY,
  GATED_CONTENT_ACCESS_UNLOCKED
} from '~/utils/paywall/gatedContentUnlockConstants';
import { cloneDeep } from '~/utils/cloneDeep';
import { isAbortedFetchError } from '~/utils/guards/isAbortedFetchError';
import { extractFetchErrorStatusCode } from '~/utils/extractFetchErrorStatusCode';
import { isContentfulPageRedirectResponse } from '~/utils/contentful/isContentfulPageRedirectResponse';
import { applyContentfulPageRedirect } from '~/utils/contentful/applyContentfulPageRedirect';
import {
  buildContentfulApiSlug,
  buildContentfulPageSyncFetchKey,
  canFetchContentfulPage,
  canReuseContentfulPageCache,
  shouldSkipContentfulPageFetch
} from '~/utils/contentful/contentfulPageSyncFetch';

export type UseContentfulPageResponseModel = {
  page: IContentPage;
  /**
   * Contains a locale slug map which maps a locale to a slug in that locale.
   * This map may contain `null` as entry whereas there actually exists
   * a matching slug, as resolving the link map is allowed to fail
   * in the api route.
   */
  localeSlugMap: Partial<Record<LOCALE_CODE, string>>;
  /**
   * Contains English topic slugs mapped by topic ID for analytics tracking
   */
  topicSlugMapEn: Record<string, string>;
  /**
   * Table of contents from H2 headings in contentSection. Omitted when showToc is false.
   */
  tocItems?: TocItem[];
  /**
   * Calculated reading time in minutes (when enabled on a hero article section)
   */
  readingTime?: number;
  /** Breadcrumb trail for the current page (from main navigation). */
  breadcrumbTrail: BreadcrumbEntry[];
  /** Minified secondary navigation (not embedded in `page.fields`). */
  secondaryNavigation?: MinifiedSecondaryNavigation;
  /** Root page teaser meta for OG tags and JSON-LD (stripped from `page.fields`). */
  pageTeaserMeta?: PageTeaserMeta;
};

const contentfulTransformer = new ContentfulTransformerService();

/** Shared `useFetch` / `useAsyncData` key — plugin is the sole fetch owner. */
export const CONTENTFUL_PAGE_FETCH_KEY = 'contentful-page';

/** Resolved page model for layout, analytics, and CMS routes. */
const CONTENTFUL_PAGE_RESOLVED_STATE_KEY = 'contentful-page-resolved';
const CONTENTFUL_PAGE_ERROR_STATE_KEY = 'contentful-page-error';

/** Last `/api/contentful` fetch error for the active route (if any). */
export function useContentfulPagePayloadError(): Ref<NuxtError | null> {
  return useState<NuxtError | null>(
    CONTENTFUL_PAGE_ERROR_STATE_KEY,
    () => null
  );
}

/**
 * Ensures `/api/contentful` has been fetched for the active route (plugin-owned).
 * Always triggers a sync when registered — do not only await a stale promise from a
 * previous route (e.g. shop → CMS cart page left `resolved` null).
 */
export async function ensureContentfulPageSyncFetch(): Promise<void> {
  const nuxtApp = useNuxtApp();
  if (nuxtApp._ensureContentfulPageSyncFetch) {
    await nuxtApp._ensureContentfulPageSyncFetch();
  }
}

/** @deprecated Prefer {@link ensureContentfulPageSyncFetch} */
export async function awaitContentfulPageSyncFetch(): Promise<void> {
  await ensureContentfulPageSyncFetch();
}

export function useContentfulPage({
  id,
  slug,
  locale,
  when
}: {
  id?: MaybeRefOrGetter<string | undefined>;
  slug?: MaybeRefOrGetter<string | undefined>;
  locale: MaybeRefOrGetter<string | undefined>;
  /**
   * When false, skips `/api/contentful` (clears async data). Used by
   * `useContentfulPageFromRoute` on shop/PDP routes while keeping the same
   * composable entry point as real CMS pages for payload deduplication.
   */
  when?: MaybeRefOrGetter<boolean>;
}): Promise<Ref<UseContentfulPageResponseModel | null>> {
  const tracer = new Tracer('composables');
  const {
    public: { isPreviewEnabled }
  } = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const { $globalPageSettings } = nuxtApp;
  const resolvedPageResponse = useState<UseContentfulPageResponseModel | null>(
    CONTENTFUL_PAGE_RESOLVED_STATE_KEY,
    () => null
  );
  const contentfulPageError = useContentfulPagePayloadError();
  const { isPaywallVisible, isValidBot } = usePaywall();

  const idResolved = computed(() => toValue(id));
  const localeResolved = computed(() => toValue(locale) ?? '');

  /**
   * Slug sent to the API, with document/software center sub-routes collapsed
   * to the root entry slug (same rules as before, but reactive).
   */
  const effectiveSlug = computed(() => {
    const raw = toValue(slug);
    if (!isDefined(raw) || raw === '') {
      return raw;
    }
    return collapseContentCenterSlug(
      raw,
      $globalPageSettings.value?.documentCenterPage?.slug ?? undefined,
      $globalPageSettings.value?.softwareCenterPage?.slug ?? undefined
    );
  });

  /** Homepage uses GPS id resolution on the API — omit empty slug from the query key. */
  const apiSlug = computed(() => buildContentfulApiSlug(effectiveSlug.value));

  /**
   * Use preloaded page from nuxt middleware
   */
  const preloaded = usePreloadedContentfulPage();
  const slugForPreload = toValue(slug);
  const preloadedSlug = preloaded.value?.page.fields.slug ?? '';
  const requestedSlug = slugForPreload ?? '';
  if (
    slugForPreload !== undefined &&
    isDefined(preloaded) &&
    isDefined(preloaded.value) &&
    preloadedSlug === requestedSlug
  ) {
    return Promise.resolve(
      preloaded as Ref<UseContentfulPageResponseModel | null>
    );
  }

  const whenResolved = computed(() =>
    when === undefined ? true : toValue(when)
  );

  return tracer.withSpan(
    'useContentfulPage',
    {
      'parameter.id': idResolved.value,
      'parameter.slug': effectiveSlug.value,
      'parameter.locale': localeResolved.value
    },
    async () => {
      // `immediate: false` so we can respect `when` (non-CMS routes) before the
      // first request. Only `plugins/26.contentfulPagePayload.ts` calls this composable;
      // CMS pages read `$contentfulPagePayload` via `useContentfulPagePayload`.
      // No AbortSignal on useFetch: the signal is bound once per shared key; aborting
      // it on PDP navigation would break browser-back restores for CMS pages.
      const fetchResult = await useFetch<ContentfulPageResponseModel>(
        '/api/contentful',
        {
          ...DEFAULT_FETCH_OPTIONS,
          key: CONTENTFUL_PAGE_FETCH_KEY,
          query: {
            id: idResolved,
            slug: apiSlug,
            locale: localeResolved,
            // Separate CDN key from the public gated response; auth stays cookie/session/bot.
            [GATED_CONTENT_ACCESS_QUERY]: computed(() =>
              isPaywallVisible.value ? undefined : GATED_CONTENT_ACCESS_UNLOCKED
            )
          },
          // no need to watch as fetch is triggered manually
          watch: false,
          dedupe: 'cancel',
          immediate: false,
          transform: (payload) => {
            if (!payload || isContentfulPageRedirectResponse(payload)) {
              return payload;
            }

            return applyContentfulAssetCdnByLocale(
              payload,
              toValue(locale) ?? undefined
            );
          }
        }
      );

      const applyRedirectFromPayload = async (
        payload: ContentfulPageResponseModel
      ): Promise<boolean> => {
        if (!isContentfulPageRedirectResponse(payload)) {
          return false;
        }

        const applied = await applyContentfulPageRedirect(
          payload.redirect,
          useRoute().path
        );

        if (applied) {
          resolvedPageResponse.value = null;
        }

        return applied;
      };

      const resolveContentfulPageResponse = (
        payload: ContentfulPageResponseModel
      ): UseContentfulPageResponseModel | null => {
        if (isContentfulPageRedirectResponse(payload)) {
          return null;
        }

        let resolvedContentPage =
          payload.page ??
          (payload.entry
            ? (resolveEntry(
                payload.entry as EntryWithLinkData<Entry<unknown>>,
                true,
                { warnRemovedReferencesInBrowser: Boolean(isPreviewEnabled) }
              ) as IContentPage)
            : null);

        if (!resolvedContentPage) {
          return null;
        }

        if (
          containsPaywall(resolvedContentPage) &&
          isPaywallVisible.value &&
          !isValidBot.value &&
          !isLeadPaywall(getPaywall(resolvedContentPage))
        ) {
          resolvedContentPage = cloneDeep(resolvedContentPage);
          moveAllEntriesAfterPaywallIntoPaywall(resolvedContentPage);
        }

        const breadcrumbTrail =
          'breadcrumbTrail' in payload && Array.isArray(payload.breadcrumbTrail)
            ? payload.breadcrumbTrail
            : [];

        const secondaryNavigation =
          payload.secondaryNavigation ??
          contentfulTransformer.minifySecondaryNavigation(
            resolvedContentPage.fields?.secondaryNavigation
          );

        if (resolvedContentPage.fields?.secondaryNavigation) {
          delete resolvedContentPage.fields.secondaryNavigation;
        }

        const pageTeaserMeta =
          payload.pageTeaserMeta ??
          getRequestScopedPageTeaserMeta(
            import.meta.server ? useRequestEvent() : undefined,
            buildPageTeaserMetaCacheKey(
              localeResolved.value,
              apiSlug.value,
              idResolved.value
            )
          );

        return {
          page: resolvedContentPage,
          localeSlugMap: payload.localeSlugMap ?? {},
          topicSlugMapEn: payload.topicSlugMapEn ?? {},
          ...(payload.tocItems !== undefined && { tocItems: payload.tocItems }),
          ...(payload.readingTime !== undefined && {
            readingTime: payload.readingTime
          }),
          breadcrumbTrail,
          ...(secondaryNavigation ? { secondaryNavigation } : {}),
          ...(pageTeaserMeta ? { pageTeaserMeta } : {})
        };
      };

      const applyResolvedPageFromFetch = (): void => {
        contentfulPageError.value = null;
        resolvedPageResponse.value = fetchResult.data.value
          ? resolveContentfulPageResponse(
              fetchResult.data.value as ContentfulPageResponseModel
            )
          : null;
      };

      let syncFetchInFlight: Promise<void> | null = null;
      let lastSyncFetchKey = '';

      const buildSyncFetchKey = (): string =>
        buildContentfulPageSyncFetchKey(
          whenResolved.value,
          idResolved.value,
          effectiveSlug.value,
          localeResolved.value
        );

      /**
       * True when the current route still needs a `/api/contentful` result and
       * neither the resolved payload nor a reusable useFetch cache entry exists.
       */
      const stillNeedsContentfulPageFetch = (): boolean => {
        if (shouldSkipContentfulPageFetch(whenResolved.value)) {
          return false;
        }
        if (!canFetchContentfulPage(idResolved.value, effectiveSlug.value)) {
          return false;
        }
        if (resolvedPageResponse.value !== null) {
          return false;
        }
        return !canReuseContentfulPageCache(
          fetchResult.data.value as ContentfulPageResponseModel | null,
          idResolved.value,
          effectiveSlug.value
        );
      };

      const syncFetch = async (options?: {
        /**
         * Bypass in-flight dedupe and cached payload reuse. Required after
         * paywall unlock: the cached `/api/contentful` response was truncated
         * by `removePaywallContent` and must be refetched with the unlock cookie.
         */
        force?: boolean;
      }): Promise<void> => {
        const force = options?.force === true;

        // Serialize syncFetch callers (route watcher + page ensure). Starting a
        // second useFetch execute with dedupe:'cancel' aborted the first — both
        // showed as (abgebrochen) and the page threw a fatal false 404 (DBP-4223).
        while (syncFetchInFlight) {
          const joinedKey = lastSyncFetchKey;
          const pending = syncFetchInFlight;
          await pending;

          const keyNow = buildSyncFetchKey();
          if (
            !force &&
            joinedKey === keyNow &&
            !stillNeedsContentfulPageFetch()
          ) {
            if (
              resolvedPageResponse.value === null &&
              canReuseContentfulPageCache(
                fetchResult.data.value as ContentfulPageResponseModel | null,
                idResolved.value,
                effectiveSlug.value
              )
            ) {
              applyResolvedPageFromFetch();
            }
            return;
          }

          // A newer flight started while we waited — join that one too.
          if (syncFetchInFlight && syncFetchInFlight !== pending) {
            continue;
          }
          break;
        }

        const requestKey = buildSyncFetchKey();
        lastSyncFetchKey = requestKey;

        // Snapshot reactive values once so run() always acts on the route state
        // that was current when this syncFetch invocation started. This prevents
        // a subtle race on the first PDP→CMS navigation: the pre-flush watcher
        // fires syncFetch() and sets syncFetchInFlight. If a stale finally from a
        // concurrent syncFetch clears syncFetchInFlight (see identity check below),
        // setup() re-enters syncFetch() with a fresh run(). Without snapshots,
        // whenResolved.value could be re-evaluated in an intermediate scheduler
        // tick where router.currentRoute hasn't fully propagated, returning false
        // for a CMS route and wiping resolvedPageResponse.
        const whenSnapshot = whenResolved.value;
        const idSnapshot = idResolved.value;
        const slugSnapshot = effectiveSlug.value;

        const run = async (): Promise<void> => {
          if (shouldSkipContentfulPageFetch(whenSnapshot)) {
            // Hide CMS payload on shop routes; keep useFetch cache for browser back.
            resolvedPageResponse.value = null;
            return;
          }
          const idV = idSnapshot;
          const slugV = slugSnapshot;
          if (!canFetchContentfulPage(idV, slugV)) {
            fetchResult.clear();
            resolvedPageResponse.value = null;
            return;
          }

          const cachedData = fetchResult.data
            .value as ContentfulPageResponseModel | null;

          if (!force && canReuseContentfulPageCache(cachedData, idV, slugV)) {
            applyResolvedPageFromFetch();
            return;
          }

          await fetchResult.execute({
            dedupe: 'cancel'
          });

          let fetchError = fetchResult.error.value;
          // External aborts (navigation away, rare residual cancel) — retry once
          // without cancelling a peer; syncFetch serialization makes mutual
          // cancel from ensure+watcher unlikely (DBP-4223).
          // After an abort `useFetch` keeps the data of the previous request, so
          // "has data" is not enough: it must belong to the requested page.
          const hasMatchingData = (): boolean =>
            canReuseContentfulPageCache(
              fetchResult.data.value as ContentfulPageResponseModel | null,
              idV,
              slugV
            );

          if (
            fetchError &&
            isAbortedFetchError(fetchError) &&
            !hasMatchingData()
          ) {
            await fetchResult.execute({
              dedupe: 'defer'
            });
            fetchError = fetchResult.error.value;
          }

          if (
            fetchError &&
            !(isAbortedFetchError(fetchError) && hasMatchingData())
          ) {
            resolvedPageResponse.value = null;
            return;
          }

          const fetchedData = fetchResult.data
            .value as ContentfulPageResponseModel | null;

          if (fetchedData && (await applyRedirectFromPayload(fetchedData))) {
            return;
          }

          applyResolvedPageFromFetch();
        };

        // Latch the in-flight gate BEFORE awaiting run() so concurrent
        // ensureContentfulPageSyncFetch / watcher calls always join (DBP-4223).
        // Identity comparison: an older finally must not clear a newer gate.
        let settleInFlight!: () => void;
        const thisPromise = new Promise<void>((resolve) => {
          settleInFlight = resolve;
        });
        syncFetchInFlight = thisPromise;
        nuxtApp._contentfulPageSyncFetch = thisPromise;
        nuxtApp._ensureContentfulPageSyncFetch = () => syncFetch();

        try {
          await run();
        } finally {
          if (syncFetchInFlight === thisPromise) {
            syncFetchInFlight = null;
          }
          settleInFlight();
        }
      };

      const stopRouteWatch = watch(
        [whenResolved, idResolved, effectiveSlug, localeResolved],
        () => {
          void syncFetch();
        },
        { immediate: false }
      );

      // Authentication or the guest unlock cookie can change paywall visibility
      // while the current page remains mounted. Refetch in both directions:
      // unlock needs the full payload, logout needs the gated payload again.
      const stopPaywallWatch = watch(isPaywallVisible, () => {
        if (
          whenResolved.value &&
          resolvedPageResponse.value?.page &&
          containsPaywall(resolvedPageResponse.value.page)
        ) {
          void syncFetch({ force: true });
        }
      });

      if (getCurrentScope()) {
        onScopeDispose(() => {
          stopRouteWatch();
          stopPaywallWatch();
        });
      }

      await syncFetch();

      const canFetchInitialPage = canFetchContentfulPage(
        idResolved.value,
        effectiveSlug.value
      );

      if (!whenResolved.value || !canFetchInitialPage) {
        contentfulPageError.value = null;
        // Always expose `resolvedPageResponse` — never a frozen `computed(() => null)`.
        // App init on PDP (`when: false`) must still update the same ref after CMS nav.
        return resolvedPageResponse;
      }

      if (fetchResult.error.value) {
        const fetchError = fetchResult.error.value;
        const statusCode = extractFetchErrorStatusCode(fetchError);

        // Let CMS routes turn missing pages into a 404 via createError in the page
        // setup — do not fatal-error from the shared plugin / useFetch path.
        if (statusCode === StatusCodes.NOT_FOUND) {
          contentfulPageError.value = null;
          return resolvedPageResponse;
        }

        if (
          isAbortedFetchError(fetchError) &&
          canReuseContentfulPageCache(
            fetchResult.data.value as ContentfulPageResponseModel | null,
            idResolved.value,
            effectiveSlug.value
          )
        ) {
          applyResolvedPageFromFetch();
          return resolvedPageResponse;
        }

        const normalizedError = createError({
          statusCode: statusCode ?? StatusCodes.INTERNAL_SERVER_ERROR,
          statusMessage: fetchError.statusMessage ?? 'Page request failed',
          cause: fetchError,
          fatal: true
        });
        contentfulPageError.value = normalizedError;
        throw normalizedError;
      }

      if (!fetchResult.data.value) {
        await fetchResult.execute({
          dedupe: 'defer'
        });
        applyResolvedPageFromFetch();
      }

      if (
        fetchResult.data.value &&
        isContentfulPageRedirectResponse(
          fetchResult.data.value as ContentfulPageResponseModel
        )
      ) {
        return resolvedPageResponse;
      }

      if (!fetchResult.data.value) {
        contentfulPageError.value = null;
        throw createError({
          statusCode: StatusCodes.NOT_FOUND,
          statusMessage: 'Page not found',
          message: 'Page not found',
          fatal: true
        });
      }

      return resolvedPageResponse;
    }
  );
}
